// GenToken/tokenUtils.js

/**
 * Format token amount based on decimals
 * @param {number} amount Token amount as a raw number
 * @param {number} decimals Number of decimal places the token has
 * @returns {string} Formatted token amount with proper decimal placement
 */
export const formatTokenAmount = (amount, decimals) => {
    if (typeof amount !== 'number' || typeof decimals !== 'number') {
      return '0';
    }
    
    // Convert to string and handle scientific notation
    const amountStr = amount.toString();
    
    // Check if in scientific notation
    if (amountStr.includes('e')) {
      // Handle scientific notation
      const [mantissa, exponent] = amountStr.split('e');
      const exponentNum = parseInt(exponent, 10);
      
      if (exponentNum < 0) {
        // Small number (e.g., 1e-9)
        const absExp = Math.abs(exponentNum);
        const zeros = '0'.repeat(absExp - 1);
        return `0.${zeros}${mantissa.replace('.', '')}`;
      } else {
        // Large number (e.g., 1e9)
        const mantissaStr = mantissa.replace('.', '');
        const zerosToAdd = exponentNum - (mantissaStr.length - 1);
        return `${mantissaStr}${'0'.repeat(Math.max(0, zerosToAdd))}`;
      }
    }
    
    // For regular numbers, divide by 10^decimals
    const divisor = Math.pow(10, decimals);
    const formattedAmount = (amount / divisor).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    });
    
    return formattedAmount;
  }
  
  /**
   * Calculate raw token amount (with decimals applied)
   * @param {number} displayAmount Amount as displayed to users
   * @param {number} decimals Number of decimal places the token has
   * @returns {number} Raw token amount with decimals applied
   */
  export const calculateRawAmount = (displayAmount, decimals) => {
    if (typeof displayAmount !== 'number' || typeof decimals !== 'number') {
      return 0;
    }
    
    // Multiply by 10^decimals to get the raw amount
    return displayAmount * Math.pow(10, decimals);
  };
  
  /**
   * Truncate a Solana address or signature for display
   * @param {string} address The full address or signature
   * @param {number} prefixLen Length of prefix to show (default: 4)
   * @param {number} suffixLen Length of suffix to show (default: 4)
   * @returns {string} Truncated address with ellipsis
   */
  export const truncateAddress = (address, prefixLen = 4, suffixLen = 4) => {
    if (!address || typeof address !== 'string') {
      return '';
    }
    
    if (address.length <= prefixLen + suffixLen + 3) {
      return address;
    }
    
    return `${address.slice(0, prefixLen)}...${address.slice(-suffixLen)}`;
  };
  
  /**
   * Get explorer URL for a token or transaction
   * @param {string} type Type of item ('token', 'tx', 'address')
   * @param {string} id Token address, transaction signature, or wallet address
   * @param {string} network Solana network
   * @returns {string} Explorer URL
   */
  export const getExplorerUrl = (type, id, network = 'devnet') => {
    const baseUrl = 'https://explorer.solana.com';
    const clusterParam = network !== 'mainnet-beta' ? `?cluster=${network}` : '';
    
    switch (type) {
      case 'token':
        return `${baseUrl}/address/${id}${clusterParam}`;
      case 'tx':
        return `${baseUrl}/tx/${id}${clusterParam}`;
      case 'address':
      default:
        return `${baseUrl}/address/${id}${clusterParam}`;
    }
  };